import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { loadAuthConfig, oidcSettings, type AuthConfig } from './config';
import {
  AuthError,
  decodeClaims,
  login as passwordLogin,
  refresh as passwordRefresh,
  renewDelay,
  type PasswordTokens,
} from './passwordClient';
import { configureApiAuth } from '@/api/client';

export interface Principal {
  readonly sub: string;
  readonly name: string;
  readonly email: string | null;
  readonly roles: readonly string[];
}

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly principal: Principal | null;
  readonly error: string | null;
  readonly signIn: () => void;
  readonly signOut: () => void;
  /** Present only in `password` mode, where sign-in takes credentials. */
  readonly signInWithPassword?: (username: string, password: string) => Promise<void>;
  readonly busy?: boolean;
  /** Directory being authenticated against, from GET /api/v1/auth/config. */
  readonly realm?: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const MOCK_PRINCIPAL: Principal = {
  sub: 'analyst@iohealth',
  name: 'Local Analyst',
  email: 'analyst@iohealth.com',
  roles: ['analyst'],
};

interface TokenClaims {
  readonly name?: string;
  readonly preferred_username?: string;
  readonly email?: string;
  readonly realm_access?: { readonly roles?: readonly string[] };
}

function toPrincipal(user: User): Principal {
  const profile = user.profile as unknown as TokenClaims & { sub?: string };
  return {
    sub: profile.sub ?? 'unknown',
    name: profile.name ?? profile.preferred_username ?? 'Unknown user',
    email: profile.email ?? null,
    roles: profile.realm_access?.roles ?? [],
  };
}

function MockAuthProvider({ children }: { children: ReactNode }): JSX.Element {
  useEffect(() => {
    configureApiAuth(
      () => 'mock-development-token',
      () => undefined,
    );
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status: 'authenticated',
      principal: MOCK_PRINCIPAL,
      error: null,
      realm: 'mock',
      signIn: () => undefined,
      signOut: () => undefined,
    }),
    [],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function OidcAuthProvider({
  children,
  config,
}: {
  children: ReactNode;
  config: AuthConfig;
}): JSX.Element {
  const managerRef = useRef<UserManager | null>(null);
  if (managerRef.current === null) {
    managerRef.current = new UserManager({
      ...oidcSettings(config),
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    });
  }
  const manager = managerRef.current;

  const [status, setStatus] = useState<AuthStatus>('loading');
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);

  const signIn = useCallback((): void => {
    void manager.signinRedirect({ state: { returnTo: window.location.pathname } });
  }, [manager]);

  const signOut = useCallback((): void => {
    tokenRef.current = null;
    void manager.signoutRedirect();
  }, [manager]);

  useEffect(() => {
    configureApiAuth(
      () => tokenRef.current,
      () => {
        // A 401 despite a token means renew failed or the session was revoked.
        // Try a silent renew once; fall back to a full redirect.
        void manager
          .signinSilent()
          .then((user) => {
            if (user) {
              tokenRef.current = user.access_token;
            } else {
              signIn();
            }
          })
          .catch(() => {
            setStatus('unauthenticated');
            setError('Your session ended. Sign in again to continue.');
          });
      },
    );
  }, [manager, signIn]);

  useEffect(() => {
    let cancelled = false;

    const adopt = (user: User | null): void => {
      if (cancelled) return;
      if (user && !user.expired) {
        tokenRef.current = user.access_token;
        setPrincipal(toPrincipal(user));
        setStatus('authenticated');
        setError(null);
      } else {
        tokenRef.current = null;
        setPrincipal(null);
        setStatus('unauthenticated');
      }
    };

    const bootstrap = async (): Promise<void> => {
      try {
        if (window.location.pathname === '/auth/callback') {
          const user = await manager.signinCallback();
          window.history.replaceState({}, '', '/');
          adopt(user ?? null);
          return;
        }
        adopt(await manager.getUser());
      } catch (cause) {
        if (cancelled) return;
        setStatus('error');
        setError(cause instanceof Error ? cause.message : 'Sign-in failed.');
      }
    };

    void bootstrap();

    const onLoaded = (user: User): void => adopt(user);
    const onUnloaded = (): void => adopt(null);
    manager.events.addUserLoaded(onLoaded);
    manager.events.addUserUnloaded(onUnloaded);
    manager.events.addSilentRenewError(() => {
      setError('Could not refresh your session in the background.');
    });

    return () => {
      cancelled = true;
      manager.events.removeUserLoaded(onLoaded);
      manager.events.removeUserUnloaded(onUnloaded);
    };
  }, [manager]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, principal, error, realm: config.realm, signIn, signOut }),
    [status, principal, error, config.realm, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Resolves auth config before deciding what to render.
 *
 * The mode determines which sign-in UI is correct, so rendering anything before
 * it is known would flash the wrong form. A failure is surfaced, never
 * downgraded to `mock`.
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadAuthConfig().then(
      (resolved) => {
        if (!cancelled) setConfig(resolved);
      },
      (cause: unknown) => {
        if (cancelled) return;
        setConfigError(cause instanceof Error ? cause.message : 'Could not load sign-in settings.');
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  if (configError) {
    return (
      <AuthContext.Provider
        value={{
          status: 'error',
          principal: null,
          error: configError,
          signIn: () => window.location.reload(),
          signOut: () => undefined,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  if (!config) {
    return (
      <AuthContext.Provider
        value={{
          status: 'loading',
          principal: null,
          error: null,
          signIn: () => undefined,
          signOut: () => undefined,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  switch (config.mode) {
    case 'oidc':
      return <OidcAuthProvider config={config}>{children}</OidcAuthProvider>;
    case 'password':
      return <PasswordAuthProvider config={config}>{children}</PasswordAuthProvider>;
    default:
      return <MockAuthProvider>{children}</MockAuthProvider>;
  }
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>.');
  return context;
}

/**
 * Direct password grant, for realms that expose a login endpoint instead of a
 * redirect flow.
 *
 * The access token lives in a ref -- in memory, never localStorage. It is a
 * bearer credential and localStorage is readable by any XSS on the origin.
 */
function PasswordAuthProvider({
  children,
  config,
}: {
  children: ReactNode;
  config: AuthConfig;
}): JSX.Element {
  const tokensRef = useRef<PasswordTokens | null>(null);
  const timerRef = useRef<number | null>(null);

  const [status, setStatus] = useState<AuthStatus>('unauthenticated');
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const clearTimer = useCallback((): void => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const signOut = useCallback((): void => {
    clearTimer();
    tokensRef.current = null;
    setPrincipal(null);
    setStatus('unauthenticated');
  }, [clearTimer]);

  const adopt = useCallback((tokens: PasswordTokens): void => {
    tokensRef.current = tokens;
    const claims = decodeClaims(tokens.accessToken);
    setPrincipal({
      // `sub` is the stable id; never key anything on email.
      sub: claims?.sub ?? 'unknown',
      name: claims?.name ?? claims?.preferredUsername ?? 'Signed in',
      email: claims?.email ?? null,
      roles: claims?.roles ?? [],
    });
    setStatus('authenticated');
    setError(null);
  }, []);

  // Schedule a refresh ahead of expiry so an in-flight query never sees a 401.
  const scheduleRenew = useCallback(
    (tokens: PasswordTokens): void => {
      clearTimer();
      if (!tokens.refreshToken || !config.refreshUrl) return;
      timerRef.current = window.setTimeout(() => {
        void passwordRefresh(config, tokens.refreshToken as string)
          .then((next) => {
            adopt(next);
            scheduleRenew(next);
          })
          .catch(() => {
            setError('Your session expired. Sign in again to continue.');
            signOut();
          });
      }, renewDelay(tokens.expiresAt));
    },
    [adopt, clearTimer, config, signOut],
  );

  const signInWithPassword = useCallback(
    async (username: string, password: string): Promise<void> => {
      setBusy(true);
      setError(null);
      try {
        const tokens = await passwordLogin(config, username, password);
        adopt(tokens);
        scheduleRenew(tokens);
      } catch (cause) {
        setStatus('unauthenticated');
        setError(
          cause instanceof AuthError
            ? cause.message
            : cause instanceof Error
              ? cause.message
              : 'Sign-in failed.',
        );
      } finally {
        setBusy(false);
      }
    },
    [adopt, config, scheduleRenew],
  );

  useEffect(() => {
    configureApiAuth(
      () => tokensRef.current?.accessToken ?? null,
      () => {
        // A 401 means the token is gone or revoked. Drop it and re-prompt
        // rather than retrying against a credential we know is dead.
        setError('Your session ended. Sign in again to continue.');
        signOut();
      },
    );
  }, [signOut]);

  useEffect(() => clearTimer, [clearTimer]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      principal,
      error,
      busy,
      realm: config.realm,
      signIn: () => setStatus('unauthenticated'),
      signOut,
      signInWithPassword,
    }),
    [status, principal, error, busy, config.realm, signOut, signInWithPassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
